import React, { useState, useEffect } from 'react';
import { MessageSquare, Check, X, DollarSign, Filter } from 'lucide-react';
import { adminGetFeedback, adminReviewFeedback } from '../../services/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export function AdminFeedback() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    fetchFeedback();
  }, [statusFilter]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const res = await adminGetFeedback(status);
      setFeedback(res.data || []);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (feedbackId, status, amount = null) => {
    try {
      await adminReviewFeedback(feedbackId, status, amount);
      toast.success(`Feedback ${status}`);
      setFeedback(feedback.filter(f => f.id !== feedbackId));
    } catch (error) {
      toast.error('Failed to review feedback');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
      paid: 'bg-blue-500/20 text-blue-400'
    };
    return colors[status] || colors.pending;
  };

  const getCategoryBadge = (category) => {
    const colors = {
      bug: 'bg-red-500/20 text-red-400',
      feature: 'bg-blue-500/20 text-blue-400',
      improvement: 'bg-purple-500/20 text-purple-400',
      general: 'bg-gray-500/20 text-gray-400'
    };
    return colors[category] || colors.general;
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-teko">Feedback Management</h2>
        <span className="text-sm text-[var(--text-secondary)]">{feedback.length} items</span>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 animate-pulse">
              <div className="h-5 w-1/3 bg-[var(--border)] rounded mb-3" />
              <div className="h-4 w-full bg-[var(--border)] rounded mb-2" />
              <div className="h-4 w-2/3 bg-[var(--border)] rounded" />
            </div>
          ))
        ) : feedback.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-secondary)]">
            <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
            <p>No feedback found</p>
          </div>
        ) : (
          feedback.map(item => (
            <div key={item.id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-medium text-lg">{item.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    by {item.username} • {new Date(item.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryBadge(item.category)}`}>
                    {item.category}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
                    {item.status}
                  </span>
                </div>
              </div>

              <p className="text-[var(--text-secondary)] mb-4">{item.content}</p>

              {item.status === 'pending' && (
                <div className="flex gap-2 pt-4 border-t border-[var(--border)]">
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleReview(item.id, 'approved', 5)}
                  >
                    <Check size={14} className="mr-1" />
                    Approve ($5)
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleReview(item.id, 'approved', 10)}
                  >
                    <DollarSign size={14} className="mr-1" />
                    Approve ($10)
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleReview(item.id, 'rejected')}
                  >
                    <X size={14} className="mr-1" />
                    Reject
                  </Button>
                </div>
              )}

              {item.payment_amount && (
                <div className="mt-4 pt-4 border-t border-[var(--border)]">
                  <span className="text-green-500 font-medium">Paid: ${item.payment_amount}</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminFeedback;
